import {
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { ReactNode } from "react";
import type { DateField } from "../types/timeEfficiency.types";
import { TE_COLORS } from "./theme";

/** `dataInicio`/`dataFim` trafegam como string ISO "yyyy-mm-dd" em todo o
 * resto do dashboard (vai direto pro filtro da Api) -- só aqui na borda
 * converte pra `Date` (o que o `DatePicker` espera) e de volta. */
function isoToDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToIso(date: Date | null): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export interface FilterOption {
  value: string;
  label: string;
}

const TODAS_CELULAS: FilterOption = { value: "", label: "Todas as células" };
const TODOS_COORDENADORES: FilterOption = {
  value: "",
  label: "Todos os coordenadores",
};
const TODOS_TIPOS: FilterOption = { value: "", label: "Todos os tipos" };

const FIELD_LABEL_SX = {
  fontSize: 10.5,
  fontWeight: 600,
  color: TE_COLORS.textFaint,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
} as const;

const FIELD_INPUT_SX = {
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: TE_COLORS.border },
} as const;

const MENU_PROPS = {
  PaperProps: { style: { maxHeight: 320 } },
} as const;

function Field({
  label,
  minWidth = 150,
  children,
}: {
  label: string;
  minWidth?: number;
  children: ReactNode;
}) {
  return (
    <Box display="flex" flexDirection="column" gap={0.5} sx={{ minWidth }}>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

interface FilterBarProps {
  celulaOptions: FilterOption[];
  coordenadorOptions: FilterOption[];
  tipoEventoOptions: FilterOption[];
  statusOptions: FilterOption[];
  filtroCelula: string;
  filtroCoordenador: string;
  filtroTipoEvento: string;
  filtroStatus: Set<string>;
  filtroCampoData: DateField;
  dataInicio: string;
  dataFim: string;
  onCelula: (v: string) => void;
  onCoordenador: (v: string) => void;
  onTipoEvento: (v: string) => void;
  onStatus: (v: Set<string>) => void;
  onCampoData: (v: DateField) => void;
  onDataInicio: (v: string) => void;
  onDataFim: (v: string) => void;
  onApply: () => void;
  onClear: () => void;
}

export function FilterBar({
  celulaOptions,
  coordenadorOptions,
  tipoEventoOptions,
  statusOptions,
  filtroCelula,
  filtroCoordenador,
  filtroTipoEvento,
  filtroStatus,
  filtroCampoData,
  dataInicio,
  dataFim,
  onCelula,
  onCoordenador,
  onTipoEvento,
  onStatus,
  onCampoData,
  onDataInicio,
  onDataFim,
  onApply,
  onClear,
}: FilterBarProps) {
  const isAllStatusSelected = filtroStatus.size === statusOptions.length;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-end",
        flexWrap: "wrap",
        bgcolor: TE_COLORS.card,
        border: `1px solid ${TE_COLORS.border}`,
        borderRadius: "12px",
        p: 2,
        mb: 2.5,
      }}
    >
      <Field label="Célula">
        <Select
          size="small"
          displayEmpty
          value={filtroCelula}
          onChange={(e) => onCelula(e.target.value)}
          MenuProps={MENU_PROPS}
          sx={FIELD_INPUT_SX}
        >
          {[TODAS_CELULAS, ...celulaOptions].map((c) => (
            <MenuItem key={c.value || "todas"} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="Coordenador">
        <Select
          size="small"
          displayEmpty
          value={filtroCoordenador}
          onChange={(e) => onCoordenador(e.target.value)}
          MenuProps={MENU_PROPS}
          sx={FIELD_INPUT_SX}
        >
          {[TODOS_COORDENADORES, ...coordenadorOptions].map((c) => (
            <MenuItem key={c.value || "todos"} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="Tipo de Evento">
        <Select
          size="small"
          displayEmpty
          value={filtroTipoEvento}
          onChange={(e) => onTipoEvento(e.target.value)}
          MenuProps={MENU_PROPS}
          sx={FIELD_INPUT_SX}
        >
          {[TODOS_TIPOS, ...tipoEventoOptions].map((c) => (
            <MenuItem key={c.value || "todos-tipos"} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="Status atual" minWidth={170}>
        <Select
          multiple
          displayEmpty
          size="small"
          value={Array.from(filtroStatus)}
          onChange={(e) => {
            const selected = e.target.value;
            const next =
              typeof selected === "string" ? selected.split(",") : selected;
            onStatus(new Set(next));
          }}
          renderValue={(selected) => {
            if (!selected.length) return "Nenhum selecionado";
            if (selected.length === statusOptions.length)
              return "Todos selecionados";
            return (
              <Chip
                size="small"
                label={`${selected.length} selecionado(s)`}
                sx={{ height: 20, fontSize: 11 }}
              />
            );
          }}
          MenuProps={MENU_PROPS}
          sx={FIELD_INPUT_SX}
        >
          <Box
            display="flex"
            gap={1}
            px={1.5}
            py={1}
            borderBottom={`1px solid ${TE_COLORS.border}`}
          >
            <Button
              size="small"
              disabled={isAllStatusSelected}
              onClick={() =>
                onStatus(new Set(statusOptions.map((o) => o.value)))
              }
            >
              Selecionar todos
            </Button>
            <Button
              size="small"
              disabled={!filtroStatus.size}
              onClick={() => onStatus(new Set())}
            >
              Limpar
            </Button>
          </Box>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value} dense>
              <Checkbox size="small" checked={filtroStatus.has(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </Field>

      <Field label="Filtrar por" minWidth={110}>
        <Select
          size="small"
          value={filtroCampoData}
          onChange={(e) => onCampoData(e.target.value as DateField)}
          MenuProps={MENU_PROPS}
          sx={FIELD_INPUT_SX}
        >
          <MenuItem value="inicio">Data de início</MenuItem>
          <MenuItem value="criacao">Data de criação</MenuItem>
        </Select>
      </Field>

      <Field label="Data início" minWidth={152}>
        <DatePicker
          value={isoToDate(dataInicio)}
          onChange={(date) => {
            // Só propaga quando a data está completa/válida -- enquanto o
            // usuário ainda está digitando (em vez de clicar no calendário),
            // `date` vem incompleto/null a cada tecla; propagar isso
            // disparava os endpoints com uma data vazia no meio da digitação.
            if (date && !Number.isNaN(date.getTime())) {
              onDataInicio(dateToIso(date));
            }
          }}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              size: "small",
              sx: { ...FIELD_INPUT_SX, width: 152 },
            },
          }}
        />
      </Field>

      <Field label="Data fim" minWidth={152}>
        <DatePicker
          value={isoToDate(dataFim)}
          onChange={(date) => {
            if (date && !Number.isNaN(date.getTime())) {
              onDataFim(dateToIso(date));
            }
          }}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              size: "small",
              sx: { ...FIELD_INPUT_SX, width: 152 },
            },
          }}
        />
      </Field>

      <Button
        variant="contained"
        size="small"
        onClick={onApply}
        sx={{ height: 40 }}
      >
        Aplicar filtro
      </Button>

      <Button
        variant="outlined"
        size="small"
        onClick={onClear}
        sx={{ height: 40 }}
      >
        Limpar
      </Button>
    </Box>
  );
}
